import { Button, Form, Input, InputNumber, Row } from 'antd';
import {
  createRoleTypeFamily,
  deleteRoleTypeFamily,
  toggleRoleTypeFamilyActivation,
  updateRoleTypeFamily
} from 'api/RoleTypeApi';
import { createScoutTag, deleteScoutTag, toggleTagActivation, updateScoutTag } from 'api/scoutTagApi';
import { useUser } from 'auth/UserHooks';
import ButtonRow from 'components/common/ButtonRow';
import React, { useEffect, useState } from 'react';
import { RoleTypeFamily } from 'types/RoleTypeFamily';
import { ScoutTag } from 'types/ScoutTag';
import displayErrorNotification from 'utils/displayErrorNotification';
import './RoleTypeFamilyForm.css';

type RoleTypeFamilyFormProps = {
  family?: RoleTypeFamily;
  onSubmit: Function;
  onCancel: Function;
  form: any;
};

const RoleTypeFamilyForm = ({ family, onSubmit, onCancel, form }: RoleTypeFamilyFormProps) => {
  const user = useUser();
  const isCreateMode = !!!family;
  const [inactive, setInactive] = useState<boolean>(false);
  const { getFieldDecorator } = form;
  const initialValues = isCreateMode
    ? {
        name: '',
        description: '',
        displayOrder: null,
        inactive: false
      }
    : { ...family };

  useEffect(() => {
    form.resetFields();
    if (!isCreateMode) {
      setInactive(family.inactive);
    }
  }, [family, onSubmit]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();

    form.validateFields((err: any, values: any) => {
      if (err) return;

      const newFamily: RoleTypeFamily = {
        id: isCreateMode ? null : family.id,
        name: values.name,
        description: values.description,
        displayOrder: values.displayOrder,
        inactive: isCreateMode ? false : family.inactive
      };
      if (isCreateMode) {
        createRoleTypeFamily(newFamily)
          .then(() => onSubmit())
          .catch(displayErrorNotification);
      } else if (newFamily.id !== null) {
        updateRoleTypeFamily(newFamily.id, newFamily)
          .then(() => onSubmit())
          .catch(displayErrorNotification);
      }
    });
  };

  const _toggleRoleTypeFamilyActivation = () => {
    if (family && family.id !== null)
      toggleRoleTypeFamilyActivation(family.id)
        .then(() => onSubmit())
        .catch(displayErrorNotification);
  };

  const _deleteRoleTypeFamily = () => {
    if (family && family.id !== null)
      deleteRoleTypeFamily(family.id)
        .then(() => onSubmit())
        .catch(displayErrorNotification);
  };

  return (
    <div style={{ minWidth: '450px' }}>
      <h2>{isCreateMode ? 'Create' : 'Edit'} Role Type Family</h2>
      <Form onSubmit={handleSubmit} className="editRoleTypeFamilyForm">
        <Form.Item label="Name" required>
          {getFieldDecorator('name', {
            initialValue: initialValues.name,
            rules: [{ required: true, message: 'Name is a required field' }]
          })(<Input disabled={inactive} maxLength={50} />)}
        </Form.Item>
        <Form.Item label="Description">
          {getFieldDecorator('description', {
            initialValue: initialValues.description
          })(<Input.TextArea disabled={inactive} rows={4} maxLength={255} />)}
        </Form.Item>
        <Form.Item label="Display Order">
          {getFieldDecorator('displayOrder', {
            initialValue: initialValues.displayOrder
          })(<InputNumber disabled={inactive} min={1} max={32767} />)}
        </Form.Item>
        <Row>
          <ButtonRow style={{ marginTop: '30px' }}>
            <Button type="primary" htmlType="submit" disabled={inactive}>
              Save
            </Button>
            <Button onClick={() => onCancel()}>Cancel</Button>
            {!isCreateMode && (
              <Button onClick={_toggleRoleTypeFamilyActivation}>{inactive ? 'Activate' : 'Deactivate'}</Button>
            )}
            {!isCreateMode && inactive && user.permissions.canDeleteTags && (
              <Button type="danger" onClick={_deleteRoleTypeFamily}>
                Delete
              </Button>
            )}
          </ButtonRow>
        </Row>
      </Form>
    </div>
  );
};

export default Form.create<RoleTypeFamilyFormProps>({ name: 'roletypefamily_form' })(RoleTypeFamilyForm);
